import type { SlideFieldsFragment } from "@/generated/graphql";
import { useChangeSlideType } from "@/lib/hooks/slides/editor/useChangeSlideType";
import { useEditorActions } from "@/lib/hooks/slides/editor/useEditorActions";
import type { SlideTemplate } from "@ai-teach/types";
import { Button, IconButton, Modal } from "@ai-teach/ui-web";
import { AiOutlineClear } from "react-icons/ai";
import { FaRegSave } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import { MdRedo, MdUndo } from "react-icons/md";

interface EditorActionsModalProps {
  slide: SlideFieldsFragment | undefined;
}

export function EditorActionsRightbar({ slide }: EditorActionsModalProps) {
  const {
    template,
    showWarningModal,
    handleTemplateChange,
    handleConfirmChange,
    handleCancelChange,
    CREATABLE_TEMPLATES,
    getTemplateLabel,
  } = useChangeSlideType({ slide });

  const {
    isSaving,
    name,
    canUndo,
    canRedo,
    updateElementsLoading,
    handleSave,
    handleUndo,
    handleRedo,
    handleClear,
    handleLeavePage,
    handleNameChange,
  } = useEditorActions();

  return (
    <>
      {/* Fixed Right Sidebar */}
      <div className="w-72 bg-background border-l border-slate-200 flex flex-col flex-shrink-0">
        <div className="flex flex-col items-stretch gap-4 px-5 py-5">
          {/* Header */}
          <div className="flex items-center justify-between ">
            <p className="text-xs w-full border-b pb-1 font-bold uppercase tracking-wider">
              Slide Actions
            </p>
          </div>

          {/* Slide Configuration */}
          <div className="space-y-1">
            {/* Template selector */}
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block px-1">
              Template
            </label>
            <select
              className="w-full text-sm font-medium text-slate-900 px-1 py-1 rounded-md border-2 border-slate-200 hover:border-primary-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 focus:outline-none transition-all duration-200 bg-slate-50/50 hover:bg-white shadow-sm"
              name="template"
              id="template"
              value={template ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value as SlideTemplate)}
            >
              {/* A legacy-only template (never offered for creation) still has to render as the
                  slide's current value, or this controlled select would silently show another. */}
              {template && !CREATABLE_TEMPLATES.includes(template) && (
                <option value={template}>{getTemplateLabel(template)}</option>
              )}
              {CREATABLE_TEMPLATES.map((value) => (
                <option key={value} value={value}>
                  {getTemplateLabel(value)}
                </option>
              ))}
            </select>

            {/* Slide name input */}
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block px-1">
              Slide Name
            </label>
            <input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full text-sm font-medium text-slate-900 px-2 py-1 rounded-md border-2 border-slate-200 hover:border-primary-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 focus:outline-none transition-all duration-200 bg-slate-50/50 hover:bg-white shadow-sm"
              placeholder="Untitled slide"
              aria-label="Slide name"
            />
          </div>

          {/* History Actions */}
          <div>
            <p className="text-xs border-t pt-1 font-bold uppercase tracking-wider block px-1">
              History
            </p>
            <div className="flex items-center justify-evenly gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUndo}
                disabled={!canUndo}
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
              >
                <MdUndo className="w-5 h-5" />
                <span className="text-sm font-medium">Undo</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRedo}
                disabled={!canRedo}
                title="Redo (Ctrl+Shift+Z)"
                aria-label="Redo"
              >
                <MdRedo className="w-5 h-5" />
                <span className="text-sm font-medium">Redo</span>
              </Button>
            </div>
          </div>

          {/* Save Action */}
          <div className="space-y-2">
            <label className="text-xs border-t pt-1 font-bold uppercase tracking-wider block px-1">
              Quick Actions
            </label>
            {/* Clear Canvas */}
            <button
              type="button"
              onClick={handleClear}
              disabled={updateElementsLoading || isSaving}
              className="btn btn-danger w-full"
            >
              <span className="btn-inner flex items-center justify-center gap-2.5">
                <AiOutlineClear className="w-4 h-4" />
                <span className="label">Clear All</span>
              </span>
            </button>
            {/* Save Changes */}
            <Button
              onClick={() => handleSave(true)}
              disabled={updateElementsLoading || isSaving}
              className="btn btn-primary w-full"
            >
              <FaRegSave className="w-4 h-4" />
              <span className="label">
                {updateElementsLoading || isSaving ? "Saving..." : "Save Changes"}
              </span>
            </Button>
            {/* Save & Exit */}
            <Button
              onClick={() => handleLeavePage(true)}
              variant="secondary"
              disabled={updateElementsLoading || isSaving}
              className="w-full"
            >
              <div className="flex justify-center items-center gap-2">
                <FiLogOut className="w-4 h-4" />
                <div className="label">
                  {updateElementsLoading || isSaving ? "Saving..." : "Save & Exit"}
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Warning Modal */}
      <Modal
        isOpen={showWarningModal}
        onClose={handleCancelChange}
        title="Warning !"
        style="danger"
        size="sm"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          {/* Icon and Message */}
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm text-slate-600">
                Changing the <span className="font-semibold">template</span> will permanently delete
                all elements on the canvas. This action cannot be undone.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={handleCancelChange} className="flex-1">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmChange} className="flex-1">
              Continue
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
